import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  DecideSeverityChangeRequestBody,
  FindingDetailDto,
} from '@terra/shared-contracts-findings';
import type { InternalEnrichedFinding } from '@terra/ui-design-system';
import { terraProxyApiClient } from '../../clients/testerClient';
import { FINDING_BY_ID_QUERY_KEY } from './useFindingById';
import { FINDINGS_QUERY_KEY } from './useFindings';
import { FINDINGS_COUNT_QUERY_KEY } from './findings-matching-count-query';
import { FINDING_AUDIT_LOGS_QUERY_KEY } from '../useFindingAuditLogs';
import { FINDING_TIMELINE_QUERY_KEY } from '../useFindingTimeline';

const applyDecidedFindingToCache = ({
  current,
  decided,
}: {
  current: InternalEnrichedFinding;
  decided: FindingDetailDto;
}): InternalEnrichedFinding => ({
  ...current,
  severity: decided.severity,
  pendingProposedSeverity: decided.pendingProposedSeverity,
  severityChangeRequest: decided.severityChangeRequest,
});

export const useDecideSeverityChangeRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      findingId,
      requestId,
      body,
    }: {
      findingId: string;
      requestId: string;
      body: DecideSeverityChangeRequestBody;
    }) => {
      const response = await terraProxyApiClient.decideSeverityChangeRequest({
        findingId,
        requestId,
        body,
      });

      if (response.status !== 200) {
        throw new Error(
          `decideSeverityChangeRequest failed with status ${response.status}`,
        );
      }

      return response.body;
    },
    onSuccess: (decided, variables) => {
      queryClient.setQueriesData<InternalEnrichedFinding>(
        {
          queryKey: [FINDING_BY_ID_QUERY_KEY, variables.findingId],
          exact: false,
        },
        (current) =>
          current ? applyDecidedFindingToCache({ current, decided }) : current,
      );

      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: [FINDING_BY_ID_QUERY_KEY, variables.findingId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [FINDINGS_QUERY_KEY],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [FINDINGS_COUNT_QUERY_KEY],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [FINDING_AUDIT_LOGS_QUERY_KEY, variables.findingId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: [FINDING_TIMELINE_QUERY_KEY, variables.findingId],
          exact: false,
        }),
      ]);
    },
  });
};
